"use client";
import { useState, useCallback } from "react";
import { ToastData, ToastType } from "@/components/ui/Toast";

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback(
    (
      type: ToastType,
      title: string,
      options?: {
        message?: string;
        duration?: number;
        action?: {
          label: string;
          onClick: () => void;
        };
      }
    ) => {
      const id = Math.random().toString(36).substr(2, 9);
      const newToast: ToastData = {
        id,
        type,
        title,
        message: options?.message,
        duration: options?.duration || 5000,
        action: options?.action,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback(
    (title: string, options?: { message?: string; duration?: number }) =>
      addToast("success", title, options),
    [addToast]
  );

  const error = useCallback(
    (title: string, options?: { message?: string; duration?: number }) =>
      addToast("error", title, options),
    [addToast]
  );

  const warning = useCallback(
    (title: string, options?: { message?: string; duration?: number }) =>
      addToast("warning", title, options),
    [addToast]
  );

  const info = useCallback(
    (title: string, options?: { message?: string; duration?: number }) =>
      addToast("info", title, options),
    [addToast]
  );

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    warning,
    info,
  };
};

export default useToast;
